import { useState, useCallback, useEffect } from "react";
import { RefreshCw } from "lucide-react";

export interface Assumptions {
  vacancy_rate_pct: number;
  expense_growth_rate_pct: number;
  going_in_cap_rate: number;
  exit_cap_rate: number;
  discount_rate: number;
  market_rent_growth_pct_annual: number;
  capital_reserves_psf: number;
  credit_loss_pct: number;
  selling_costs_pct: number;
  renewal_probability_pct: number;
  downtime_months: number;
  lease_term_years: number;
  new_tenant_ti_psf: number;
  renewal_tenant_ti_psf: number;
  analysis_start_date: string;
  expense_growth_rates?: Record<string, number>;
  cpi_rate?: number;
  purchase_price: number;
  hold_period_years: number;
  loan_amount: number;
  interest_rate: number;
  amortization_years: number;
  io_period_years: number;
}

/** Jan 1 of current year — matches backend default in property_builder.py */
export function defaultStartDate(): string {
  return `${new Date().getFullYear()}-01-01`;
}

/** Build API payload — nest rollover fields and start date properly */
export function buildApiPayload(a: Assumptions): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    vacancy_rate_pct: a.vacancy_rate_pct,
    expense_growth_rate_pct: a.expense_growth_rate_pct,
    exit_cap_rate: a.exit_cap_rate,
    discount_rate: a.discount_rate,
    market_rent_growth_pct_annual: a.market_rent_growth_pct_annual,
    capital_reserves_psf: a.capital_reserves_psf,
    credit_loss_pct: a.credit_loss_pct,
    selling_costs_pct: a.selling_costs_pct,
    // analysis_start_date intentionally NOT included: the start date is a
    // deal-level anchor (buildDealAssumptionsPatch). Embedding it in every
    // scenario save froze stale dates into scenario assumptions, which then
    // override (mask) later deal-level date changes in the engine.
    expense_growth_rates: a.expense_growth_rates ?? {},
    cpi_rate: a.cpi_rate ?? null,
    rollover_assumptions: {
      renewal_probability_pct: a.renewal_probability_pct,
      downtime_months: a.downtime_months,
      market_rent_growth_pct_annual: a.market_rent_growth_pct_annual,
      lease_term_years: a.lease_term_years,
      new_tenant_ti_psf: a.new_tenant_ti_psf,
      renewal_tenant_ti_psf: a.renewal_tenant_ti_psf,
    },
  };
  if (a.loan_amount > 0) {
    payload.debt = {
      loan_amount: a.loan_amount,
      interest_rate: a.interest_rate,
      amortization_years: a.amortization_years,
      io_period_years: a.io_period_years,
      origination_fee_pct: 0,
      recourse: false,
    };
  }
  return payload;
}

/** Build deal-level assumption patch (for purchase_price, exit_cap, debt) */
export function buildDealAssumptionsPatch(a: Assumptions): Record<string, unknown> {
  const patch: Record<string, unknown> = {
    purchase_price: a.purchase_price,
    going_in_cap_rate: a.going_in_cap_rate,
    exit_cap_rate: a.exit_cap_rate,
    discount_rate: a.discount_rate,
    hold_period_years: a.hold_period_years,
    selling_costs_pct: a.selling_costs_pct,
    capital_reserves_psf: a.capital_reserves_psf,
    general_vacancy_pct: a.vacancy_rate_pct,
    credit_loss_pct: a.credit_loss_pct,
    expense_growth_rate_pct: a.expense_growth_rate_pct,
    expense_growth_rates: a.expense_growth_rates ?? {},
  };
  // Only send a real date — an empty string would be stored verbatim and
  // fail Property validation on the next projection.
  if (a.analysis_start_date) patch.analysis_start_date = a.analysis_start_date;
  patch.debt = a.loan_amount > 0
    ? {
        loan_amount: a.loan_amount,
        interest_rate: a.interest_rate,
        amortization_years: a.amortization_years,
        io_period_years: a.io_period_years,
        origination_fee_pct: 0,
        recourse: false,
      }
    : null;
  return patch;
}

function pctInput(val: number): string {
  // 2 decimal places — cap rates are routinely quoted to 1bps (5.25%,
  // 6.125%). 1 decimal was truncating user input on each hydration →
  // save cycle, so "exit at 5.25" would settle at 5.3% after one blur
  // and drift from there.
  return (val * 100).toFixed(2);
}

const EXPENSE_CATEGORIES = [
  { key: "utilities", label: "Utilities" },
  { key: "repairs_and_maintenance", label: "R&M" },
  { key: "janitorial", label: "Janitorial" },
  { key: "landscaping", label: "Landscaping" },
  { key: "management_fee", label: "Mgmt Fee" },
  { key: "administrative", label: "Admin" },
] as const;

const DEFAULT_VALUES: Record<string, string> = {
  hold_period_years: "10",
  going_in_cap_rate: "6.3",
  exit_cap_rate: "6.5",
  discount_rate: "8.0",
  expense_growth_rate_pct: "3.0",
  vacancy_rate_pct: "5.0",
  market_rent_growth_pct_annual: "3.0",
  credit_loss_pct: "1.0",
  selling_costs_pct: "1.5",
  capital_reserves_psf: "0.15",
  renewal_probability_pct: "70.0",
  downtime_months: "6",
  lease_term_years: "5",
  new_tenant_ti_psf: "35.00",
  renewal_tenant_ti_psf: "10.00",
};

function Field({ label, value, onChange, onBlur, step, suffix, type, fieldKey, min, max }: {
  label: string; value: string; onChange: (v: string) => void; onBlur: () => void;
  step?: string; suffix?: string; type?: string; fieldKey?: string; min?: string; max?: string;
}) {
  const defaultVal = fieldKey ? DEFAULT_VALUES[fieldKey] : undefined;
  const isDefault = defaultVal != null && value === defaultVal;
  return (
    <div data-field-id={fieldKey || undefined}>
      <label className="text-xs font-medium text-bark-500 block mb-1">
        {label}
        {isDefault && <span className="ml-1 text-bark-300 font-normal">(default)</span>}
      </label>
      <div className="relative">
        <input
          type={type || "number"} step={step || "0.1"} min={min} max={max}
          value={value} onChange={(e) => onChange(e.target.value)} onBlur={onBlur}
          className={`w-full py-2 text-sm border border-grove-300 rounded-lg bg-grove-50 text-bark-700 tabular-nums ${
            type === "date" ? "px-3" : "pl-3 pr-8"
          }`}
        />
        {suffix && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-bark-400">{suffix}</span>
        )}
      </div>
    </div>
  );
}

function OverrideHint({ active }: { active?: boolean }) {
  if (!active) return null;
  return (
    <p className="text-[10px] text-honey-600 mt-0.5 flex items-center gap-1">
      <span className="w-1.5 h-1.5 rounded-full bg-honey-500" />Year-level overrides active
    </p>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl border border-grove-300 shadow-sm p-5">
      <h3 className="font-display text-base font-semibold text-bark-800 mb-4">{title}</h3>
      {children}
    </div>
  );
}

interface AssumptionsPanelProps {
  assumptions: Assumptions;
  onAssumptionsChange: (a: Assumptions) => void;
  onRecalculate: (a: Assumptions) => void;
  recalculating: boolean;
  overrideKeys?: Set<string>;
  vacancyExemptSummary?: string;
}

export default function AssumptionsPanel({
  assumptions, onAssumptionsChange, onRecalculate, recalculating, overrideKeys, vacancyExemptSummary,
}: AssumptionsPanelProps) {
  const [vacancyStr, setVacancyStr] = useState("");
  const [expGrowthStr, setExpGrowthStr] = useState("");
  const [goingInCapStr, setGoingInCapStr] = useState("6.3");
  const [exitCapStr, setExitCapStr] = useState("");
  const [discountStr, setDiscountStr] = useState("");
  const [mktRentStr, setMktRentStr] = useState("");
  const [reservesStr, setReservesStr] = useState("");
  const [creditLossStr, setCreditLossStr] = useState("");
  const [sellingCostsStr, setSellingCostsStr] = useState("1.5");
  const [renewalProbStr, setRenewalProbStr] = useState("");
  const [downtimeStr, setDowntimeStr] = useState("");
  const [leaseTermStr, setLeaseTermStr] = useState("5");
  const [newTiStr, setNewTiStr] = useState("35.00");
  const [renewalTiStr, setRenewalTiStr] = useState("10.00");
  const [startDateStr, setStartDateStr] = useState(defaultStartDate());
  const [ppStr, setPpStr] = useState("0");
  const [holdStr, setHoldStr] = useState("10");
  const [loanStr, setLoanStr] = useState("0");
  const [intRateStr, setIntRateStr] = useState("0");
  const [amortStr, setAmortStr] = useState("30");
  const [ioStr, setIoStr] = useState("0");
  const [catGrowthStrs, setCatGrowthStrs] = useState<Record<string, string>>({});
  const [cpiRateStr, setCpiRateStr] = useState("");

  const initStrings = useCallback((a: Assumptions) => {
    setVacancyStr(pctInput(a.vacancy_rate_pct));
    setExpGrowthStr(pctInput(a.expense_growth_rate_pct));
    setGoingInCapStr(pctInput(a.going_in_cap_rate));
    setExitCapStr(pctInput(a.exit_cap_rate));
    setDiscountStr(pctInput(a.discount_rate));
    setMktRentStr(pctInput(a.market_rent_growth_pct_annual));
    setCpiRateStr(a.cpi_rate ? pctInput(a.cpi_rate) : "");
    setReservesStr(a.capital_reserves_psf.toFixed(2));
    setCreditLossStr(pctInput(a.credit_loss_pct));
    setSellingCostsStr(pctInput(a.selling_costs_pct));
    setRenewalProbStr(pctInput(a.renewal_probability_pct));
    setDowntimeStr(String(a.downtime_months));
    setLeaseTermStr(String(a.lease_term_years));
    setNewTiStr(a.new_tenant_ti_psf > 0 ? a.new_tenant_ti_psf.toFixed(2) : "35.00");
    setRenewalTiStr(a.renewal_tenant_ti_psf > 0 ? a.renewal_tenant_ti_psf.toFixed(2) : "10.00");
    setStartDateStr(a.analysis_start_date);
    setPpStr(a.purchase_price > 0 ? Math.round(a.purchase_price).toLocaleString("en-US") : "0");
    setHoldStr(String(a.hold_period_years));
    setLoanStr(a.loan_amount > 0 ? Math.round(a.loan_amount).toLocaleString("en-US") : "0");
    setIntRateStr(a.interest_rate > 0 ? pctInput(a.interest_rate) : "0");
    setAmortStr(String(a.amortization_years || 30));
    setIoStr(String(a.io_period_years || 0));
    const cg: Record<string, string> = {};
    if (a.expense_growth_rates) {
      for (const [k, v] of Object.entries(a.expense_growth_rates)) {
        cg[k] = (v * 100).toFixed(1);
      }
    }
    setCatGrowthStrs(cg);
  }, []);

  useEffect(() => { initStrings(assumptions); }, [assumptions, initStrings]);

  const sync = (field: keyof Assumptions, strVal: string, isPct: boolean) => {
    const num = parseFloat(strVal);
    if (!isNaN(num)) onAssumptionsChange({ ...assumptions, [field]: isPct ? num / 100 : num });
  };

  const buildFromInputs = (): Assumptions => {
    const p = (s: string, fb: number) => { const n = parseFloat(s); return isNaN(n) ? fb : n / 100; };
    const v = (s: string, fb: number) => { const n = parseFloat(s); return isNaN(n) ? fb : n; };
    const money = (s: string, fb: number) => { const n = parseFloat(s.replace(/[,$\s]/g, "")); return isNaN(n) ? fb : n; };
    return {
      vacancy_rate_pct: p(vacancyStr, assumptions.vacancy_rate_pct),
      expense_growth_rate_pct: p(expGrowthStr, assumptions.expense_growth_rate_pct),
      going_in_cap_rate: p(goingInCapStr, assumptions.going_in_cap_rate),
      exit_cap_rate: p(exitCapStr, assumptions.exit_cap_rate),
      discount_rate: p(discountStr, assumptions.discount_rate),
      market_rent_growth_pct_annual: p(mktRentStr, assumptions.market_rent_growth_pct_annual),
      capital_reserves_psf: v(reservesStr, assumptions.capital_reserves_psf),
      credit_loss_pct: p(creditLossStr, assumptions.credit_loss_pct),
      selling_costs_pct: p(sellingCostsStr, assumptions.selling_costs_pct),
      renewal_probability_pct: p(renewalProbStr, assumptions.renewal_probability_pct),
      downtime_months: v(downtimeStr, assumptions.downtime_months),
      lease_term_years: v(leaseTermStr, assumptions.lease_term_years),
      new_tenant_ti_psf: v(newTiStr, assumptions.new_tenant_ti_psf),
      renewal_tenant_ti_psf: v(renewalTiStr, assumptions.renewal_tenant_ti_psf),
      analysis_start_date: startDateStr || assumptions.analysis_start_date,
      purchase_price: money(ppStr, assumptions.purchase_price),
      hold_period_years: v(holdStr, assumptions.hold_period_years),
      loan_amount: money(loanStr, assumptions.loan_amount),
      interest_rate: p(intRateStr, assumptions.interest_rate),
      amortization_years: v(amortStr, assumptions.amortization_years),
      io_period_years: v(ioStr, assumptions.io_period_years),
      expense_growth_rates: (() => {
        const cg: Record<string, number> = {};
        for (const [k, s] of Object.entries(catGrowthStrs)) {
          const n = parseFloat(s);
          if (!isNaN(n)) cg[k] = n / 100;
        }
        return Object.keys(cg).length > 0 ? cg : undefined;
      })(),
      cpi_rate: cpiRateStr ? p(cpiRateStr, assumptions.cpi_rate ?? 0) : undefined,
    };
  };

  const handleUpdate = () => {
    const a = buildFromInputs();
    onAssumptionsChange(a);
    onRecalculate(a);
  };

  const grid2 = "grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3";
  const grid3 = "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3";

  return (
    <div className="space-y-6 print:hidden">
      {/* Sticky update button */}
      <div className="sticky top-0 z-10 bg-grove-50 pb-3 pt-1 -mx-1 px-1">
        <button onClick={handleUpdate} disabled={recalculating}
          className="w-full sm:w-auto px-8 py-2.5 text-sm font-semibold text-white bg-forest-600 rounded-lg hover:bg-forest-500 hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
          {recalculating ? (<><RefreshCw className="w-4 h-4 animate-spin" />Updating...</>) : "Update Pro Forma"}
        </button>
      </div>

      {/* 1. Property & Valuation */}
      <Section title="Property & Valuation">
        <div className={grid3}>
          <Field label="Purchase Price" value={ppStr} suffix="$" type="text" fieldKey="purchase_price"
            onChange={(v) => setPpStr(v)}
            onBlur={() => {
              const n = parseFloat(ppStr.replace(/[,$\s]/g, ""));
              if (!isNaN(n)) {
                setPpStr(Math.round(n).toLocaleString("en-US"));
                onAssumptionsChange({ ...assumptions, purchase_price: n });
              }
            }} />
          <Field label="Going-In Cap Rate" value={goingInCapStr} suffix="%" fieldKey="going_in_cap_rate" min="0.1" max="20"
            onChange={(v) => { setGoingInCapStr(v); const n = parseFloat(v); if (!isNaN(n)) setExitCapStr((n + 0.25).toFixed(2)); }}
            onBlur={() => { const n = parseFloat(goingInCapStr); if (!isNaN(n)) { const e = n + 0.25; setExitCapStr(e.toFixed(2)); onAssumptionsChange({ ...assumptions, going_in_cap_rate: n / 100, exit_cap_rate: e / 100 }); } }} />
          <Field label="Exit Cap Rate" value={exitCapStr} suffix="%" fieldKey="exit_cap_rate" min="0.1" max="20"
            onChange={setExitCapStr} onBlur={() => sync("exit_cap_rate", exitCapStr, true)} />
          <Field label="Hold Period" value={holdStr} suffix="yr" step="1" fieldKey="hold_period_years"
            onChange={setHoldStr} onBlur={() => sync("hold_period_years", holdStr, false)} />
          <Field label="Analysis Start Date" value={startDateStr} type="date"
            onChange={setStartDateStr}
            onBlur={() => { if (startDateStr) onAssumptionsChange({ ...assumptions, analysis_start_date: startDateStr }); }} />
          <div>
            <Field label="Selling Costs" value={sellingCostsStr} suffix="%" fieldKey="selling_costs_pct" min="0" max="10"
              onChange={setSellingCostsStr} onBlur={() => sync("selling_costs_pct", sellingCostsStr, true)} />
          </div>
        </div>
      </Section>

      {/* 2. Revenue */}
      <Section title="Revenue">
        <div className={grid2}>
          <div>
            <Field label="General Vacancy" value={vacancyStr} suffix="%" fieldKey="vacancy_rate_pct" min="0" max="50"
              onChange={setVacancyStr} onBlur={() => sync("vacancy_rate_pct", vacancyStr, true)} />
            <OverrideHint active={overrideKeys?.has("vacancy_rate_pct")} />
            {vacancyExemptSummary && (
              <p className="text-[10px] text-forest-600 mt-0.5">{vacancyExemptSummary}</p>
            )}
          </div>
          <Field label="Credit Loss" value={creditLossStr} suffix="%" fieldKey="credit_loss_pct" min="0" max="25"
            onChange={setCreditLossStr} onBlur={() => sync("credit_loss_pct", creditLossStr, true)} />
          <Field label="Market Rent Growth" value={mktRentStr} suffix="%" fieldKey="market_rent_growth_pct_annual"
            onChange={setMktRentStr} onBlur={() => sync("market_rent_growth_pct_annual", mktRentStr, true)} />
          <Field label="CPI Rate" value={cpiRateStr} suffix="%" fieldKey="cpi_rate"
            onChange={setCpiRateStr} onBlur={() => sync("cpi_rate", cpiRateStr, true)} />
        </div>
      </Section>

      {/* 3. Expenses */}
      <Section title="Expenses">
        <div className={grid2}>
          <div>
            <Field label="Expense Growth (Global)" value={expGrowthStr} suffix="%" fieldKey="expense_growth_rate_pct" min="0" max="15"
              onChange={setExpGrowthStr} onBlur={() => sync("expense_growth_rate_pct", expGrowthStr, true)} />
          </div>
          <div>
            <Field label="Capital Reserves" value={reservesStr} suffix="$/SF" step="0.01" fieldKey="capital_reserves_psf" min="0"
              onChange={setReservesStr} onBlur={() => sync("capital_reserves_psf", reservesStr, false)} />
            <OverrideHint active={overrideKeys?.has("capital_reserves_psf")} />
          </div>
        </div>
        {/* Per-category expense growth — always visible */}
        <div className="mt-4 pt-4 border-t border-grove-200">
          <h4 className="text-xs font-semibold text-bark-500 uppercase tracking-wide mb-3">
            Per-Category Growth Rates
            {Object.keys(catGrowthStrs).length > 0 && (
              <span className="ml-2 text-forest-600 normal-case tracking-normal font-semibold">
                {Object.keys(catGrowthStrs).length} override{Object.keys(catGrowthStrs).length !== 1 && "s"}
              </span>
            )}
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
            {EXPENSE_CATEGORIES.map((cat) => (
              <div key={cat.key} className="flex items-center gap-3">
                <label className="text-xs text-bark-500 w-20 shrink-0">{cat.label}</label>
                <div className="relative flex-1">
                  <input type="number" step="0.1"
                    value={catGrowthStrs[cat.key] ?? ""}
                    onChange={(e) => {
                      setCatGrowthStrs((prev) => {
                        const next = { ...prev };
                        if (e.target.value === "") delete next[cat.key];
                        else next[cat.key] = e.target.value;
                        return next;
                      });
                    }}
                    onBlur={() => {
                      const val = catGrowthStrs[cat.key];
                      const n = val ? parseFloat(val) : NaN;
                      const cg = { ...(assumptions.expense_growth_rates ?? {}) };
                      if (!isNaN(n)) cg[cat.key] = n / 100;
                      else delete cg[cat.key];
                      onAssumptionsChange({ ...assumptions, expense_growth_rates: Object.keys(cg).length > 0 ? cg : undefined });
                    }}
                    placeholder={expGrowthStr || "—"}
                    className="w-full py-2 text-sm border border-grove-300 rounded-lg bg-grove-50 text-bark-700 tabular-nums pl-3 pr-8"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-bark-400">%</span>
                </div>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-bark-300 mt-2">Blank = use global rate</p>
        </div>
      </Section>

      {/* 4. Rollover */}
      <Section title="Rollover">
        <div className={grid3}>
          <Field label="Renewal Probability" value={renewalProbStr} suffix="%" fieldKey="renewal_probability_pct" min="0" max="100"
            onChange={setRenewalProbStr} onBlur={() => sync("renewal_probability_pct", renewalProbStr, true)} />
          <Field label="Downtime" value={downtimeStr} suffix="mo" step="1" fieldKey="downtime_months" min="0" max="36"
            onChange={setDowntimeStr} onBlur={() => sync("downtime_months", downtimeStr, false)} />
          <Field label="New Lease Term" value={leaseTermStr} suffix="yr" step="1" fieldKey="lease_term_years" min="1" max="30"
            onChange={setLeaseTermStr} onBlur={() => sync("lease_term_years", leaseTermStr, false)} />
          <Field label="TI — New Tenant" value={newTiStr} suffix="$/SF" step="0.50" fieldKey="new_tenant_ti_psf" min="0" max="200"
            onChange={setNewTiStr} onBlur={() => sync("new_tenant_ti_psf", newTiStr, false)} />
          <Field label="TI — Renewal" value={renewalTiStr} suffix="$/SF" step="0.50" fieldKey="renewal_tenant_ti_psf" min="0" max="200"
            onChange={setRenewalTiStr} onBlur={() => sync("renewal_tenant_ti_psf", renewalTiStr, false)} />
        </div>
      </Section>

      {/* 5. Debt */}
      <Section title="Debt">
        <div className="mb-4">
          <label className="block text-xs font-semibold text-bark-500 uppercase tracking-wide mb-2">Quick Finance</label>
          <div className="flex flex-wrap gap-2">
            {[
              { label: "65% LTV", ltv: 0.65, rate: 5.50, amort: 30, io: 2 },
              { label: "55% LTV", ltv: 0.55, rate: 5.25, amort: 30, io: 0 },
              { label: "IO Only", ltv: 0.65, rate: 5.75, amort: 30, io: parseInt(holdStr) || 10 },
              { label: "No Debt", ltv: 0, rate: 0, amort: 0, io: 0 },
            ].map((preset) => {
              const pp = parseFloat(ppStr.replace(/[,$\s]/g, "")) || 0;
              return (
                <button key={preset.label} onClick={() => {
                  const loanAmt = preset.ltv > 0 ? Math.round(pp * preset.ltv) : 0;
                  setLoanStr(String(loanAmt));
                  setIntRateStr(preset.rate > 0 ? preset.rate.toFixed(1) : "0");
                  setAmortStr(String(preset.amort));
                  setIoStr(String(preset.io));
                  const next = { ...assumptions, loan_amount: loanAmt, interest_rate: preset.rate / 100, amortization_years: preset.amort, io_period_years: preset.io };
                  onAssumptionsChange(next);
                  onRecalculate(next);
                }}
                  className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-grove-300 text-bark-600 hover:bg-grove-50 hover:border-honey-400 transition-colors">
                  {preset.label}
                </button>
              );
            })}
          </div>
        </div>
        <div className={grid2}>
          <Field label="Loan Amount" value={loanStr} suffix="$" type="text" fieldKey="debt.loan_amount"
            onChange={(v) => setLoanStr(v)}
            onBlur={() => {
              const n = parseFloat(loanStr.replace(/[,$\s]/g, ""));
              if (!isNaN(n)) {
                setLoanStr(Math.round(n).toLocaleString("en-US"));
                onAssumptionsChange({ ...assumptions, loan_amount: n });
              }
            }} />
          <Field label="Interest Rate" value={intRateStr} suffix="%" min="0" max="15" fieldKey="debt.interest_rate"
            onChange={setIntRateStr} onBlur={() => sync("interest_rate", intRateStr, true)} />
          <Field label="Amortization" value={amortStr} suffix="yr" step="1" min="1" max="40" fieldKey="debt.amortization_years"
            onChange={setAmortStr} onBlur={() => sync("amortization_years", amortStr, false)} />
          <Field label="IO Period" value={ioStr} suffix="yr" step="1" min="0" max="10" fieldKey="debt.io_period_years"
            onChange={setIoStr} onBlur={() => sync("io_period_years", ioStr, false)} />
        </div>
      </Section>

      {/* 6. Return Targets — Exit Cap moved to Property & Valuation
           section where users expect it; Discount Rate stays here. */}
      <Section title="Return Targets">
        <div className={grid2}>
          <Field label="Discount Rate" value={discountStr} suffix="%" fieldKey="discount_rate" min="0" max="30"
            onChange={setDiscountStr} onBlur={() => sync("discount_rate", discountStr, true)} />
        </div>
      </Section>
    </div>
  );
}
